import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function PortfolioDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/projects/${slug}`)
      .then((r) => setProject(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-32 text-center" data-testid="project-notfound">
        <h1 className="text-4xl font-bold text-white">Project not found</h1>
        <Link to="/portfolio" className="mt-6 inline-flex text-fuchsia-400 hover:text-white">
          ← Back to portfolio
        </Link>
      </div>
    );
  }

  if (!project) {
    return <div className="mx-auto max-w-7xl px-6 py-32 text-white/60">Loading…</div>;
  }

  return (
    <div data-testid="project-detail-page">
      <section className="relative overflow-hidden px-6 pb-10 pt-16 md:pt-24">
        <div className="absolute inset-0 radial-glow" />
        <div className="relative mx-auto max-w-7xl">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            <ArrowLeft size={14} /> All projects
          </Link>
          <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-fuchsia-300">
            <span>{project.category}</span>
            {project.year && <><span className="text-white/20">·</span><span className="text-white/50">{project.year}</span></>}
          </div>
          <h1 className="mt-3 text-5xl font-bold tracking-tighter text-white md:text-7xl" data-testid="project-title">
            {project.title}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/70">{project.summary}</p>
        </div>
      </section>

      {project.cover_image && (
        <section className="px-6 py-10">
          <div className="mx-auto max-w-7xl overflow-hidden rounded-3xl border border-white/10">
            <img src={project.cover_image} alt={project.title} className="w-full object-cover" />
          </div>
        </section>
      )}

      <section className="px-6 py-14">
        <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">Overview</p>
            <div className="mt-3 whitespace-pre-line text-base leading-relaxed text-white/80 md:text-lg">
              {project.description}
            </div>
          </div>
          <aside className="md:col-span-4">
            <div className="sticky top-24 rounded-2xl border border-white/10 bg-[#131B2F] p-6">
              <dl className="space-y-4 text-sm">
                {project.client && (
                  <div>
                    <dt className="text-white/40">Client</dt>
                    <dd className="mt-1 text-white">{project.client}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-white/40">Services</dt>
                  <dd className="mt-1 text-white">{project.category}</dd>
                </div>
                {project.tags?.length > 0 && (
                  <div>
                    <dt className="text-white/40">Tags</dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {project.tags.map((t) => (
                        <span key={t} className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-white/70">
                          {t}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {project.gallery?.length > 0 && (
        <section className="px-6 py-10">
          <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2">
            {project.gallery.map((g, i) => (
              <img key={i} src={g} alt="" className="rounded-2xl border border-white/10" />
            ))}
          </div>
        </section>
      )}

      <section className="px-6 pb-24 pt-10">
        <div className="mx-auto max-w-7xl rounded-3xl border border-white/10 bg-[#131B2F] p-10 md:p-14">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <h3 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
              Like this? <span className="gradient-text">Let's make yours.</span>
            </h3>
            <Link to="/contact" className="inline-flex rounded-full gradient-bg px-6 py-3 text-sm font-semibold text-white" data-testid="project-cta">
              Start a project
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
